import Phaser from 'phaser';
import { GAME } from '../constants';
import { createRunFromProfile } from '../run/RunState';
import { getNextUnlockHint, loadMetaProfile } from '../meta/Profile';
import { PIXEL_THEME, FONT_FAMILY, createPixelPanel, drawPixelBackdrop } from '../ui/PixelTheme';
import { fadeIn, fadeOut } from '../ui/SceneTransitions';
import { PROLOGUE } from '../story/Lore';

export class MainMenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MainMenuScene' });
  }

  create(): void {
    fadeIn(this);
    const profile = loadMetaProfile();
    const cx = GAME.WIDTH / 2;
    const heroY = 88;

    drawPixelBackdrop(this);

    const marquee = createPixelPanel(this, cx, heroY, 700, 118, {
      fill: 0x1c1627,
      stroke: 0xc56f4f,
      accent: 0xffd68f,
    });
    marquee.elements.forEach(el => el.setDepth(1));

    this.add.text(cx + 2, heroY - 8 + 2, 'TEN-ISH', {
      fontSize: '40px',
      fontFamily: FONT_FAMILY,
      color: '#2b1a1b',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(3);
    this.add.text(cx, heroY - 8, 'TEN-ISH', {
      fontSize: '40px',
      fontFamily: FONT_FAMILY,
      color: '#ffe29a',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(4);

    this.add.text(cx, heroY + 38,
      'The Ledger broke. Climb it. Balance what you can.\nNumbers. Zones. Combos. Survival.', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#e5e9ff',
      align: 'center',
      lineSpacing: 5,
    }).setOrigin(0.5).setDepth(4);

    const startPanel = createPixelPanel(this, cx, 196, 360, 58, {
      fill: 0x241a2a,
      stroke: 0xff8756,
      accent: 0xffd88c,
    });
    startPanel.elements.forEach(el => el.setDepth(5));
    const startBtn = this.add.text(cx, 196, '[ START NEW RUN ]', {
      fontSize: '14px',
      fontFamily: FONT_FAMILY,
      color: '#fff6dc',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(6);

    startPanel.bg.setInteractive({ useHandCursor: true });
    startPanel.bg.on('pointerover', () => {
      startBtn.setColor('#ffe9b5');
      startPanel.bg.setStrokeStyle(3, 0xffd166);
    });
    startPanel.bg.on('pointerout', () => {
      startBtn.setColor('#fff6dc');
      startPanel.bg.setStrokeStyle(2, 0xff7f50);
    });
    const beginRun = () => {
      const run = createRunFromProfile(profile);
      fadeOut(this).then(() =>
        this.scene.start('StoryScene', { beat: PROLOGUE, next: 'MapScene', nextData: { run } }),
      );
    };
    startPanel.bg.on('pointerdown', beginRun);
    this.input.keyboard?.on('keydown-ENTER', beginRun);
    this.input.keyboard?.on('keydown-SPACE', beginRun);
    this.tweens.add({
      targets: startPanel.elements,
      alpha: 0.9,
      duration: 900,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // Left panel: run flow
    const leftPanel = createPixelPanel(this, cx - 226, 382, 420, 236, {
      fill: 0x152434,
      stroke: 0x59c2b2,
      accent: 0x95fff0,
    });
    leftPanel.elements.forEach(el => el.setDepth(1));
    this.add.text(cx - 226, 288, 'RUN LOOP', {
      fontSize: '12px',
      fontFamily: FONT_FAMILY,
      color: '#95fff0',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);
    this.add.text(cx - 420, 318,
      '1. Choose a path on the map\n' +
      '2. Assign cards to zones\n' +
      '3. Place cards in ATK / DEF / ABL\n' +
      '4. End turn and resolve outcomes\n' +
      '5. Pick Number / Ability / Augment\n' +
      '6. Survive elites, curses, and bosses',
      {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#d0dcff',
        lineSpacing: 6,
      }).setDepth(2);

    // Right panel: depth explanation
    const rightPanel = createPixelPanel(this, cx + 226, 382, 420, 236, {
      fill: 0x241b33,
      stroke: 0xe4774f,
      accent: 0xffc77d,
    });
    rightPanel.elements.forEach(el => el.setDepth(1));
    this.add.text(cx + 226, 288, 'HOW BUILD DEPTH WORKS', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#ffd79c',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(2);
    this.add.text(cx + 34, 318,
      '- Zone caps can increase or shrink\n' +
      '- Curses are real cards with penalties\n' +
      '- Enemy stances telegraph next pressure\n' +
      '- Statuses and mutators change each turn\n' +
      '- Elites guarantee stronger rewards\n' +
      '- Milestones unlock new content packs',
      {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#f0e4c5',
        lineSpacing: 7,
      }).setDepth(2);

    // Profile panel
    const profilePanel = createPixelPanel(this, cx, 658, GAME.WIDTH - 104, 104, {
      fill: 0x191c2c,
      stroke: 0x5f7399,
      accent: 0x98b5ef,
    });
    profilePanel.elements.forEach(el => el.setDepth(1));
    this.add.text(cx, 626,
      `Profile: Runs ${profile.stats.runs} | Wins ${profile.stats.wins} | Elites ${profile.stats.elites} | Bosses ${profile.stats.bosses} | Combos ${profile.stats.combos}`,
      {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: PIXEL_THEME.textMuted,
      }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 648,
      `Unlocks: max rarity ${profile.unlocks.maxRarity.toUpperCase()} | event pack ${profile.unlocks.eventPack} | curse pack ${profile.unlocks.cursePack} | ability pack ${profile.unlocks.abilityPack}`,
      {
        fontSize: '11px',
        fontFamily: FONT_FAMILY,
        color: '#a0d3ff',
      }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 672, `Next unlock: ${getNextUnlockHint(profile)}`, {
      fontSize: '11px',
      fontFamily: FONT_FAMILY,
      color: PIXEL_THEME.textAccent,
      align: 'center',
      wordWrap: { width: GAME.WIDTH - 220 },
    }).setOrigin(0.5).setDepth(2);

    this.add.text(cx, 692, 'Press ENTER or SPACE to start', {
      fontSize: '10px',
      fontFamily: FONT_FAMILY,
      color: '#95a8d6',
    }).setOrigin(0.5).setDepth(2);
  }
}
